import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import Typography from '../Typography/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { translate } from '../../locales';
import NoteIcon from './icons/NoteIcon';
import TipIcon from './icons/TipIcon';
import Tooltip from '../Tooltip/Tooltip';
import { noteClasses as classes } from './noteClasses';
import { PaperRoot, HowToIcon } from './Note.style';

const iconMapping = {
  tip: <TipIcon aria-label="tip icon" />,
  note: <NoteIcon aria-label="note icon" />,
  howTo: <HowToIcon aria-label="how to icon" />,
  banner: <HowToIcon aria-label="how to icon" />
};

const hasCloseIconMapping = {
  tip: true,
  note: false,
  howTo: true,
  banner: true
};

const Note = forwardRef((props, ref) => {
  const {
    children,
    closedButtonTitle = '',
    icon,
    onClose,
    variant = 'note',
    title: incomingTitle,
    maxWidth,
    ...rest
  } = props;

  const hasCloseIcon = onClose && hasCloseIconMapping[variant];
  const defaultTitle = `${translate(`note.${variant}`)}: `;
  const title = incomingTitle ?? defaultTitle;

  return (
    <PaperRoot
      maxWidth={maxWidth}
      hasCloseIcon={hasCloseIcon}
      isBanner={variant === 'banner'}
      ref={ref}
      data-qa-id="note-root"
      {...rest}>
      <div className={classes.content}>
        <div className={classes.icon} data-qa-id="note-icon">
          {icon || iconMapping[variant]}
        </div>
        <div className={classes.message} data-qa-id="note-message">
          <Typography weight="bold" variant="body2" className={classes.variant}>
            {title}
          </Typography>
          {children}
        </div>
      </div>
      {hasCloseIcon ? (
        <div className={classes.action}>
          <Tooltip title={closedButtonTitle}>
            <IconButton
              data-qa-id="note-close"
              size="small"
              aria-label={closedButtonTitle || 'close note'}
              color="inherit"
              onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ) : null}
    </PaperRoot>
  );
});

Note.propTypes = {
  children: PropTypes.node,
  closedButtonTitle: PropTypes.string,
  variant: PropTypes.oneOf(['tip', 'note', 'howTo', 'banner']),
  /**
   * Override the title displayed before the text.
   */
  title: PropTypes.string,
  /**
   * Override the icon displayed before the children.
   */
  icon: PropTypes.node,
  /**
   * When provided and no `action` prop is set, a close icon button is displayed that triggers the callback when clicked.
   *
   * Signature:
   *function(event: object) => void
   *event: The event source of the callback.
   */
  onClose: PropTypes.func,
  /**
   * The ARIA role attribute of the element.
   */
  maxWidth: PropTypes.number
};

Note.classes = classes;
Note.displayName = 'Note';

export default Note;
